import React, { useState, useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import Inline from 'yet-another-react-lightbox/plugins/inline';
import PropTypes from 'prop-types';
import VerticalTab from '../VerticalTab';
import SecondaryButton from '../SecondaryButton';
import PrimaryButton from '../PrimaryButton';
import DropDown from '../DropDown';
import useApi from '../../hooks/useApi';
import useLocalStorage from '../../hooks/useLocalStorage';
import SpinningIcon from '../../../public/assets/images/theme-2/loops-icon-white.svg';
import { getLanguages, getTargetAudiences, articleLengths, imageCounts, getYesNoValues } from '../../utils';
import {
  AIHelperTab,
  OuterWrapper,
  ContentWrapper,
  SubjectField,
  Heading,
  SettingsWrapper,
  GalleryWrapper,
  Placeholder,
} from './styles';

const AIHelper = ({ userId, userToken, flash, nodeId, nodeType, availableLocales, countryCode }) => {
  const defaultLanguage = availableLocales[window.locale];
  const getText = (key, params) => `${i18n.__('openai.' + key, params)}`;
  const getKey = (prop) => `aiHelper_${prop}_${userId}`;

  const [prompt, setPrompt] = useLocalStorage(getKey('prompt'), '');
  const [language, setLanguage] = useLocalStorage(getKey('language'), defaultLanguage);
  const [targetAudience, setTargetAudience] = useLocalStorage(getKey('targetAudience'), getText('unspecified'));
  const [size, setSize] = useLocalStorage(getKey('size'), 200);
  const [imageCount, setImageCount] = useLocalStorage(getKey('imageCount'), 10);
  const [findImages, setFindImages] = useLocalStorage(getKey('findImages'), false);

  const [forceOpen, setForceOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(-1);
  const [images, setImages] = useState([]);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [disabled, setDisabled] = useState(!nodeType);
  const [isNodePanel, setIsNodePanel] = useState(!!nodeType);
  const { postData, loading } = useApi(userToken);

  const panelOpened = (event) => {
    const nodePanel = event.detail.type === 1;
    setIsNodePanel(nodePanel);
    setDisabled(!nodePanel);
  };

  const runQuery = async () => {
    setImages([]);
    const settings = {
      prompt,
      type: nodeType,
      language,
      targetAudience,
      size,
      imageCount,
      findImages,
    };
    const response = await postData(`/api/v3/openai/nodeQuery/${nodeId}`, { settings });
    if (!response.success) {
      console.error(response);
      setDisabled(!isNodePanel);
      if (response.message === 'auth_failed') {
        flash(i18n.__('app_error_unauthorized'));
      } else {
        flash(i18n.__('app_error_alert'));
      }
    } else {
      const { answer, images } = response.body;
      setImages(images);
      document.dispatchEvent(new CustomEvent('nodeContentGenerated', { detail: answer }));
    }
  };

  const copyToContent = () => {
    document.dispatchEvent(new CustomEvent('insertContentPicture', { detail: images[imageIndex] }));
  };

  useEffect(() => {
    document.addEventListener('didShow.panel', panelOpened);
    return () => {
      document.removeEventListener('didShow.panel', panelOpened);
    };
  }, []);

  useEffect(() => {
    if (images?.length > 0) {
      setImageIndex(0);
    }
  }, [images]);

  const texts = {
    tabheader: i18n.__('ai_helper.tab_title'),
    header: i18n.__('ai_helper.header'),
    subheader: i18n.__('ai_helper.prompt_header'),
    footer: '',
  };

  const onClose = () => {
    setForceOpen(false);
  };

  const languages = getLanguages(availableLocales);
  const targetAudiences = getTargetAudiences(countryCode, i18n);
  const yesNoValues = getYesNoValues(i18n);

  const generalSettings = (
    <>
      <Heading>{getText('general_settings')}</Heading>
      <SettingsWrapper>
        <DropDown
          id="language"
          label={getText('language')}
          items={languages}
          selectedValue={language}
          maxWidth={200}
          onChange={(event) => setLanguage(event.target.value)}
          disabled={disabled || loading}
        />
        <DropDown
          id="targetAudience"
          label={getText('target_audience')}
          items={targetAudiences}
          selectedValue={targetAudience || targetAudiences[0].value}
          maxWidth={200}
          onChange={(event) => setTargetAudience(event.target.value)}
          disabled={disabled || loading}
        />
      </SettingsWrapper>
      <SettingsWrapper>
        <DropDown
          id="articleLength"
          label={getText('node_info_length')}
          items={articleLengths}
          selectedValue={size}
          onChange={(event) => setSize(event.target.value)}
          disabled={disabled || loading}
        />
        <DropDown
          id="findImages"
          label={getText('find_images')}
          items={yesNoValues}
          selectedValue={findImages}
          maxWidth={100}
          onChange={(event) => setFindImages(event.target.value)}
          disabled={disabled || loading}
        />
        <DropDown
          id="imageCount"
          label={getText('image_count')}
          items={imageCounts}
          selectedValue={imageCount}
          maxWidth={100}
          onChange={(event) => setImageCount(event.target.value)}
          disabled={disabled || loading}
        />
      </SettingsWrapper>
    </>
  );

  const contentSection = (
    <ContentWrapper>
      <SubjectField
        autoFocus={true}
        id="prompt"
        name="prompt"
        aria-required={true}
        defaultValue={prompt}
        placeholder={i18n.__('ai_helper.prompt_placeholder')}
        onChange={(event) => setPrompt(event.target.value)}
        disabled={disabled || loading}
      />
      {generalSettings}
      <SettingsWrapper>
        <SecondaryButton
          text={i18n.__('ai_helper.generate_content')}
          onClick={runQuery}
          disabled={disabled || loading || !prompt}
        />
        {loading && <Placeholder icon={SpinningIcon} />}
      </SettingsWrapper>
      {images.length > 0 && (
        <>
          <GalleryWrapper>
            <Lightbox
              index={imageIndex}
              slides={images}
              plugins={[Counter, Inline]}
              counter={{ container: { style: { top: 'unset', bottom: '16px', left: 'unset', right: 'unset' } } }}
              carousel={{
                padding: 0,
                spacing: 0,
                imageFit: 'cover',
              }}
              on={{
                click: () => setOpenLightbox(true),
                view: ({ index }) => setImageIndex(index),
              }}
              inline={{
                style: {
                  width: '100%',
                  maxWidth: '900px',
                  aspectRatio: '3 / 2',
                  margin: '0 auto',
                },
              }}
              styles={{ container: { cursor: 'zoom-in' } }}
            />
            <Lightbox
              open={openLightbox}
              close={() => setOpenLightbox(false)}
              on={{
                view: ({ index }) => setImageIndex(index),
              }}
              index={imageIndex}
              slides={images}
              animation={{ fade: 0 }}
              controller={{ closeOnPullDown: true, closeOnBackdropClick: true }}
            />
          </GalleryWrapper>
          <PrimaryButton
            text={i18n.__('ai_helper.insert_image')}
            onClick={copyToContent}
            disabled={disabled || loading}
          />
        </>
      )}
    </ContentWrapper>
  );

  return (
    <VerticalTab
      id="ai-helper"
      order={2}
      forceOpen={forceOpen}
      TabComponent={AIHelperTab}
      WrapperComponent={OuterWrapper}
      openClassName="ai-helper-open"
      texts={texts}
      content={contentSection}
      onClose={onClose}
    />
  );
};

AIHelper.propTypes = {
  userId: PropTypes.string.isRequired,
  userToken: PropTypes.string.isRequired,
  flash: PropTypes.func.isRequired,
  useApiHook: PropTypes.func,
};

export default AIHelper;
